import BoltIcon from '@mui/icons-material/Bolt';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import FlagIcon from '@mui/icons-material/Flag';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { alpha, Badge, Box, Chip, IconButton, Menu, MenuItem, Paper, Stack, type Theme, Tooltip, Typography, useTheme } from '@pulse/ui';
import { memo, useState } from 'react';

import type { HomeFormDialogAction, HomeWorkRow } from '../types';
import { projectColor } from '../utils/projectColor';

function todayIso(): string {
  return new Date().toLocaleDateString('en-CA');
}

function tint(color: string) {
  return { color, bgcolor: alpha(color, 0.1), borderColor: alpha(color, 0.35) };
}

// Same "guess a semantic bucket from the step name" heuristic as the Planner's
// statusChipColor() — this workflow is DB-configurable, so status names aren't a fixed enum.
// A renamed step just falls through to the neutral default; nothing breaks.
function statusStyle(theme: Theme, status: string) {
  const s = status.toLowerCase();
  if (s.includes('done') || s.includes('closed')) return tint(theme.palette.success.main);
  if (s.includes('review')) return tint('#423699'); // brand "Review" purple — no theme slot
  if (s.includes('progress')) return tint(theme.palette.secondary.dark);
  if (s.includes('block') || s.includes('hold')) return tint(theme.palette.error.main);
  return tint(theme.palette.text.secondary);
}

export function dueDateInfo(dueDate: string | undefined): { label: string; color?: 'error.main' | 'warning.main' } {
  if (!dueDate) return { label: '—' };
  const today = todayIso();
  const tomorrow = new Date(Date.now() + 86_400_000).toLocaleDateString('en-CA');
  if (dueDate < today) return { label: new Date(`${dueDate}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), color: 'error.main' };
  if (dueDate === today) return { label: 'Today', color: 'warning.main' };
  if (dueDate === tomorrow) return { label: 'Tomorrow' };
  return { label: new Date(`${dueDate}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) };
}

export function StatusPill({ status, options, onChange }: { status: string; options: string[]; onChange: (v: string) => void }) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const style = statusStyle(theme, status);
  return (
    <>
      <Chip
        size="small"
        label={status}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        variant="outlined"
        sx={{ ...style, fontWeight: 600, cursor: 'pointer' }}
      />
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {options.map((s) => (
          <MenuItem key={s} selected={s === status} onClick={() => { onChange(s); setAnchorEl(null); }}>{s}</MenuItem>
        ))}
      </Menu>
    </>
  );
}

export interface WorkItemRowProps {
  row: HomeWorkRow;
  statusOptions: string[];
  onToggleExpedited: (row: HomeWorkRow) => void;
  /** §SA-409 3f — Expedite is a Project Lead/Manager call. Asked per row rather than per view,
   *  so a PL/PM still can't expedite a task on a project they only work on. False renders the
   *  same bolt as a read-only indicator: the state stays visible, the action doesn't. */
  canExpedite: (row: HomeWorkRow) => boolean;
  onStatusChange: (row: HomeWorkRow, toStep: string) => void;
  onLogTime: (row: HomeWorkRow) => void;
  onOpenComments: (row: HomeWorkRow) => void;
  /** HomeTabConfig.formDialogAction for the active tab — passed down rather than derived from
   *  `row.kind` here, so which kinds open a dialog (and under which icon and label) stays
   *  declared in one table (../types.ts) and this component keeps knowing nothing about Risks,
   *  Dependencies or Escalations. null means this kind has no dialog to open. */
  formDialogAction: HomeFormDialogAction | null;
  onOpenFormDialog: (row: HomeWorkRow) => void;
}

// Desktop row for any Home work-item type. Everything issue-specific (the expedited bolt, the
// flag, the logged-hours/log-time/comments cluster) hangs off `row.issue`, which only the Issue
// adapter populates — a Risk leaves those slots empty and takes the Log button instead
// (`showLog`, which comes from the tab's config rather than from the row). The slots keep
// their fixed widths either way so the Priority/Due/Status columns line up at the same x
// position on every tab, and switching tabs doesn't visibly shift the layout.
//
// Memoized because MyWorkPanel virtualizes the list and re-renders on every scroll step; rows
// already on screen have unchanged props then and are skipped.
export const WorkItemRow = memo(function WorkItemRow({ row, statusOptions, onToggleExpedited, canExpedite, onStatusChange, onLogTime, onOpenComments, formDialogAction, onOpenFormDialog }: WorkItemRowProps) {
  const theme = useTheme();
  const due = dueDateInfo(row.dueDate);
  const FormDialogIcon = formDialogAction?.icon;
  // Matches IssueDetailDialog's existing "Expedite" convention (BoltIcon, color="warning") —
  // keep the two surfaces visually consistent rather than inventing a second "expedited" color.
  const expeditedColor = theme.palette.warning.main;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        ...(row.issue?.expedited && { bgcolor: alpha(expeditedColor, 0.06), borderColor: alpha(expeditedColor, 0.4) }),
      }}
    >
      {row.issue ? (
        canExpedite(row) ? (
          <Tooltip title={row.issue.expedited ? 'Remove from Expedite' : 'Mark as Expedite'}>
            <IconButton size="small" onClick={() => onToggleExpedited(row)} aria-label={row.issue.expedited ? `Remove ${row.title} from Expedite` : `Mark ${row.title} as Expedite`}>
              {row.issue.expedited ? <BoltIcon color="warning" /> : <BoltOutlinedIcon sx={{ color: 'text.disabled' }} />}
            </IconButton>
          </Tooltip>
        ) : (
          // Read-only: an expedited item still reads as expedited, it just can't be changed here.
          // Only the "on" state is worth showing — an un-expedited row with no action to offer is
          // noise, so it falls back to the same spacer a non-issue row uses.
          <Tooltip title={row.issue.expedited ? 'Expedite' : ''}>
            <Box sx={{ width: 30, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
              {row.issue.expedited && <BoltIcon color="warning" fontSize="small" />}
            </Box>
          </Tooltip>
        )
      ) : (
        <Box sx={{ width: 30, flexShrink: 0 }} />
      )}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {row.issue?.flagged && (
            <Tooltip title="Flagged / blocked">
              <FlagIcon sx={{ fontSize: 14, color: 'error.main' }} />
            </Tooltip>
          )}
          <Typography variant="body2" fontWeight={600} noWrap>{row.title}</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: projectColor(row.projectId), flexShrink: 0 }} />
          <Typography variant="caption" color="text.secondary" noWrap>{row.projectKey} · {row.projectName}</Typography>
        </Stack>
      </Box>

      <Box sx={{ width: 96, flexShrink: 0 }}>
        {row.badge && (
          <Chip size="small" label={row.badge.label} color={row.badge.color} variant="outlined" />
        )}
      </Box>

      <Box sx={{ width: 76, flexShrink: 0 }}>
        <Typography variant="body2" fontWeight={500} color={due.color ?? 'text.secondary'}>{due.label}</Typography>
      </Box>

      <Box sx={{ width: 140, flexShrink: 0 }}>
        <StatusPill status={row.status} options={statusOptions} onChange={(toStep) => onStatusChange(row, toStep)} />
      </Box>

      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ width: 110, flexShrink: 0, justifyContent: 'flex-end' }}>
        {row.issue && (
          <>
            <Tooltip title={`${row.issue.totalLoggedHours}h logged`}>
              <Stack direction="row" alignItems="center" spacing={0.25}>
                <ScheduleIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                <Typography variant="body2" fontWeight={700}>{row.issue.totalLoggedHours}h</Typography>
              </Stack>
            </Tooltip>
            <Tooltip title="Log time">
              <IconButton size="small" onClick={() => onLogTime(row)} aria-label={`Log time on ${row.title}`}>
                <ScheduleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Comments">
              <IconButton size="small" onClick={() => onOpenComments(row)} aria-label={`Open comments on ${row.title}`}>
                <Badge badgeContent={row.issue.commentCount} color="secondary" max={9}>
                  <ChatBubbleOutlineIcon fontSize="small" />
                </Badge>
              </IconButton>
            </Tooltip>
          </>
        )}
        {/* Same slot the issue cluster occupies, so every tab's actions sit at the same x. The
            icon and label come from the tab's config — HistoryIcon/"Log" for Risk and
            Escalation, whose dialogs open onto a Log section, and the Dependencies tab's own
            icon for a Dependency, whose dialog opens the record itself. */}
        {formDialogAction && FormDialogIcon && (
          <Tooltip title={formDialogAction.label}>
            <IconButton size="small" onClick={() => onOpenFormDialog(row)} aria-label={`${formDialogAction.label} ${row.title}`}>
              <FormDialogIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Paper>
  );
});
